import React, { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";

if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
}

type InvoicePdfPreviewProps = {
  pdfBlob: Blob | null;
  loading?: boolean;
  className?: string;
  loadingMessage?: string;
  errorMessage?: string;
  emptyMessage?: string;
};

export default function InvoicePdfPreview({
  pdfBlob,
  loading = false,
  className = "",
  loadingMessage = "Loading preview...",
  errorMessage = "Unable to render PDF preview.",
  emptyMessage = "No preview available.",
}: InvoicePdfPreviewProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const container = containerRef.current;
    if (!pdfBlob || !container) {
      setRendering(false);
      setError("");
      if (container) {
        container.innerHTML = "";
      }
      return;
    }

    let cancelled = false;

    const render = async () => {
      setRendering(true);
      setError("");
      container.innerHTML = "";

      try {
        const data = await pdfBlob.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data }).promise;
        const containerWidth = container.clientWidth || 612;
        const pixelRatio = window.devicePixelRatio || 1;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) {
            return;
          }

          const page = await pdf.getPage(pageNum);
          const unscaled = page.getViewport({ scale: 1 });
          const scale = Math.min(
            2,
            (containerWidth / unscaled.width) * pixelRatio,
          );
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          if (!context) {
            continue;
          }

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = `${viewport.width / pixelRatio}px`;
          canvas.style.height = `${viewport.height / pixelRatio}px`;
          canvas.className = "mx-auto block max-w-full";

          const renderTask = page.render({
            canvasContext: context,
            viewport,
          });
          await renderTask.promise;

          if (cancelled) {
            return;
          }
          container.appendChild(canvas);
        }
      } catch {
        if (!cancelled) {
          setError(errorMessage);
          container.innerHTML = "";
        }
      } finally {
        if (!cancelled) {
          setRendering(false);
        }
      }
    };

    void render();

    return () => {
      cancelled = true;
    };
  }, [pdfBlob, errorMessage]);

  const showLoading = loading || rendering;

  if (!pdfBlob && !showLoading) {
    return (
      <div
        className={`flex min-h-[28rem] items-center justify-center text-sm text-gray-font ${className}`}
      >
        {emptyMessage}
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex min-h-[28rem] items-center justify-center px-4 text-center text-sm text-red-600 ${className}`}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-auto bg-white ${className}`}
      style={{ minHeight: "28rem", maxHeight: "28rem" }}
    >
      {showLoading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/85 text-sm text-gray-font">
          {loadingMessage}
        </div>
      ) : null}
      <div ref={containerRef} className="flex flex-col items-center gap-2 p-2" />
    </div>
  );
}
